import * as React from "react";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { bindActionCreators, compose, Dispatch } from "redux";
import ReactGA from "react-ga";

import * as ACTIONS from "./actionCreators";
import * as PAYMENT_ACTIONS from "../ThankYou/actionCreators";
import * as adminActionCreators from "../AdminDashboard/actionCreators";
import * as dynamicActionCreators from "../DynamicForm/actionCreators";

import * as CONFIG from "../../../config.json";
import Loader from "../Loader";
import {
    getCustomerIDFromStore,
    getQueryString,
    setSessionValue,
    removeSessionBeforeUnload,
    getPersistData,
    setPersistData,
    dateFormatter,
    loadGoogleMapAPI,
} from "../../utils/utils";
import { COPY_WARRANTY_DATA, CREATE_ANOTHER } from "../../constants/storageKeys";
import { RootState } from "../../rootReducer";
import IProps, { ThankYouProps, ThankYouActionProps } from "./IProps";

import "../../../assets/sass/components/thankYou.scss";

import constantFile from "../../constants/constantFile";
import { PersistData } from "../../middleware/persistDataMiddleware";
import BSTable from "../BSTable";
import * as submittedWarrantyActionCreators from "../SubmittedWarranties/actionCreators";
import * as globalActions from "../../globalStore/actions";

enum DownloadType {
    FullWarrantyPdf,
    SummaryPdf,
}

interface IStateProps {
    isCreateNewBtnClicked?: boolean;
    isNewBtnClickedChecked?: boolean;
    appUrlPath?: string;
    shouldRedirect?: boolean;
}

const varianceWaiverStatusEnum = {
    Pending: 1,
    Approved: 2,
    Denied: 3,
    Null: 4,
}

/**
 * @category Main Component
 * @class ThankYou
 * @description
 * Component will show after payment success
 * @param {IProps} props - Props of MultipleWarrantiesThankYouComponent
 */
class MultipleWarrantiesThankYouComponent extends React.Component<IProps, IStateProps> {
    constructor(props){
        super(props);
        this.state = {
            isCreateNewBtnClicked: false,
            isNewBtnClickedChecked: false,
            appUrlPath: "",
            shouldRedirect: false,
        }
    }

    componentDidMount() {
        const { fetchUserInfo, userInfo, transactionData } = this.props;
        // fetch user info if not exist
        if (userInfo && Object.keys(userInfo).length > 0) {
            if (!transactionData) this.processValidateTransaction(false);
        } else if (!userInfo || Object.keys(userInfo).length === 0) {
            fetchUserInfo({ isFromThankyouPage: true, statusCode: this.getTransactionStatus() });
        }
        loadGoogleMapAPI();
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<null>, snapshot?: any) {
        const { transactionData, error, tempContractorSelection, userInfo } = this.props;
        const {
            layoutResponse: { JSONSchema, UISchema, ErrorSchema },
            layoutResponse,
        } = this.props;

        if (prevProps.userInfo && Object.keys(prevProps.userInfo).length == 0 && Object.keys(userInfo).length > 0) {
            if (!transactionData) this.processValidateTransaction(false);
        }
        if (!prevProps.transactionData && transactionData) {
            this.initializeGA("/paymentSuccess");
        }
        if (!prevProps.tempContractorSelection && tempContractorSelection) {
            this.onTableRowClickConfirm(tempContractorSelection);
        }
        if (!prevProps.error && error) this.openCartPage();

        // Create New Click - Fetching FormLayoutJSON
        if(!this.state.isNewBtnClickedChecked && this.state.isCreateNewBtnClicked) {
            if (!JSONSchema || !UISchema || !ErrorSchema) {
                this.props.fetchFormLayoutData({ payload: "Residential Warranty" });
            }
            this.setState({
                isNewBtnClickedChecked: true,
            });
        }

        // Redirecting to location once JSON schema exists
        if(!this.state.shouldRedirect && this.state.isNewBtnClickedChecked && this.state.isCreateNewBtnClicked  && Object.keys(layoutResponse).length > 0) {
            globalActions.updateActiveScreen("NewForm");
            this.setState({
                shouldRedirect: true,
            });
            if(userInfo.isAdmin) {
                window.location.replace(this.state.appUrlPath);
            } else {
                this.props.history.push(this.state.appUrlPath);
            }
        }

    }

    handleResize = () => {
        this.props.updateScreenResolution({ width: window.innerWidth, height: window.innerHeight });
    };

    /**
     * Process Validate Transaction
     */
    processValidateTransaction(makeonlyReviewData): void {
        const { transactionData, validateTransaction } = this.props;
        const id = this.getTransactionId();
        if (!transactionData && !makeonlyReviewData) {
            if (id) {
                validateTransaction({ id, status: this.getTransactionStatus(), isMakeValidateTransaction: true });
            } else {
                this.openDashboardPage();
            }
        } else if (transactionData && !id) {
            validateTransaction({
                id: transactionData.warrantyId.toString() || "",
                status: 0,
                isMakeValidateTransaction: false,
            });
        }
    }

    /**
     * getTransactionId from the `ref` query string param
     * @return {string} ref
     */
    getTransactionId = (): string => {
        //const param = new URLSearchParams(this.props.location.search);
        //return param.get("ref");
        return getQueryString("ref", window.location.href);
    };

    /**
     * getTransactionStatus
     * paymentsuccess: 1, paymenterror: 2, paymentcancel: 3
     * @return {number} status - either 1 / 2 / 3
     */
    getTransactionStatus = (): number => {
        const pathname = this.props.location.pathname;
        return pathname.includes("paymentsuccess") ? 1 : pathname.includes("paymentcancel") ? 3 : 2;
    };

    /**
     * initialize google analysis
     * @param {string} pageview
     */
    initializeGA = (pageview) => {
        ReactGA.initialize(CONFIG["CONFIG"].googleAnalyticsID);
        // ReactGA.pageview(pageview);
    };

    /**
     * Open dashboard page (DraftRegistration View)
     * @return {void}
     */
    openDashboardPage = (): void => {
        removeSessionBeforeUnload();
        const customerId = getCustomerIDFromStore(false);
        const path = customerId ? `/?customerID=${customerId}` : "/?provider=okta";
        this.props.history.push(path);
        return;
    };

    openCartPage = () => {
        this.props.history.push("/myCart");
    };

    openSubmittedTab = (): void => {
        const {
            updateActiveScreen,
            updateActiveTab,
            updateIsAlreadyPaymentInitiated,
            resetIsAlreadyPaymentInitiated,
        } = this.props;
        updateIsAlreadyPaymentInitiated();
        resetIsAlreadyPaymentInitiated();
        const persistData: PersistData = getPersistData() || {};
        delete persistData.isAlreadyPaymentInitiated;
        setPersistData(persistData);
        updateActiveScreen("draftList");
        updateActiveTab("submitted");
        this.openDashboardPage();
    };

    onTableRowClickConfirm = (TableRowSelection) => {
        const { updateContractorSelection } = this.props;
        // Modify the contractor Selection as needed
        const contractorObject = {};
        contractorObject["activeStatus"] = TableRowSelection.isActive ? "Active" : "Inactive";
        contractorObject["contractorId"] = TableRowSelection.contractorId;
        contractorObject["contractorName"] = TableRowSelection.contractorName;
        contractorObject["customerId"] = TableRowSelection.customerId;
        const contractorAddress = TableRowSelection.addresses.filter((address) => {
            // address.addressType &&
            return address.addressType === "1" || address.addressType === null;
        });
        contractorObject["region"] = contractorAddress.length
            ? contractorAddress[0].city + ", " + contractorAddress[0].stateOrProvince
            : "N/A";
        updateContractorSelection(contractorObject);
        // this.props.history.push(`/?contractorId=${TableRowSelection.contractorId}`);
    };

    /**
     * Capture google analysis and open download pdf link
     * @param {DownloadType} downloadType
     */
    setGADownload = (downloadType: DownloadType) => {
        const { warrantyId } = this.props.transactionData;
        if (downloadType === DownloadType.FullWarrantyPdf) {
            ReactGA.event({
                category: "warranty confirmation",
                action: "warranty confirmation: download full warranty pdf",
                label: `warranty confirmation: download without warranty cost: ${warrantyId}`,
            });
        } else if (downloadType === DownloadType.SummaryPdf) {
            ReactGA.event({
                category: "warranty confirmation",
                action: "warranty confirmation: download summary pdf",
                label: `warranty confirmation: download: ${warrantyId}`,
            });
        }
    };

    /**
     * Download full warranty report
     * Fetch pdf from server
     */
    downloadFullPdf = (obj): void => {
        const { generateFullWarrantyPdf } = this.props;
        generateFullWarrantyPdf(obj.id, obj.warrantyNum);
        this.setGADownload(DownloadType.FullWarrantyPdf);
    };


    downloadSummaryPdf = (obj): void => {
        const { generateSummaryWarrantyPdf, downloadWarrantySummaryPdf } = this.props;
        const warrantynumber = obj.warrantyNum;
        const warrantyId= obj.id;
        const fileName = `Warranty-Summary_${warrantynumber}.pdf`;
        downloadWarrantySummaryPdf(warrantyId, warrantynumber);
        // generateSummaryWarrantyPdf(warrantyId, warrantynumber)
        this.setGADownload(DownloadType.SummaryPdf);
    }

    componentWillUnmount() {
        this.props.clearTransactionData();
        const persistData: PersistData = getPersistData() || {};
        delete persistData.shoppingCartId;
        setPersistData(persistData);
    }

    downloadAllClick = (e) => {
        this.props.downloadAllWarranties();
    };

    createNew = () => {
        const { userInfo, resetIsAlreadyPaymentInitiated } = this.props;
        const customerId = decodeURIComponent(getCustomerIDFromStore(true));
        let path = customerId ? `/?customerID=${customerId}` : "/";
        if (userInfo && userInfo.isAdmin) {
            path = "/?provider=okta";
        }
        // Removing isPaymentInitiated from State to fetch FormLayout JSON after making payment
        resetIsAlreadyPaymentInitiated();
        let persistData: PersistData = getPersistData() || {};
        delete persistData.isAlreadyPaymentInitiated;
        setPersistData(persistData);
        setSessionValue(CREATE_ANOTHER, "true");

        this.setState({
            isCreateNewBtnClicked: true,
            isNewBtnClickedChecked: false,
            shouldRedirect: false,
            appUrlPath: path
        });
    };

    copyMe = (obj) => {
        const warrantyId = obj.id;
        let persistData: PersistData = getPersistData() || {};
        persistData = Object.assign(persistData, {
            warrantyId,
        });
        delete persistData.isAlreadyPaymentInitiated;
        setPersistData(persistData);
        setSessionValue(COPY_WARRANTY_DATA, "true");
        this.createNew();
    };
    onPrintReceiptClick = () => {
        let w = 400;
        let h = 400; // default sizes
        const popupWinWidth = 632;
        const popupWinHeight = 500;
        w = popupWinWidth + 30;
        h = popupWinHeight + 30;
        // window.open(`/receipt/${transactionId}`, "Payment Receipt", 'location=no,width=' + w + ',height=' + h + ',menubar=no,scrollbars=no,frame=false');
        this.popupWindow(
            `${CONFIG.CONFIG.pdReceiptUrl}/receipt/${this.getTransactionId()}`,
            "Payment Receipt",
            window,
            w,
            h
        );
    };
    popupWindow = (url, windowName, win, w, h) => {
        const y = win.top.outerHeight / 2 + win.top.screenY - h / 2;
        const x = win.top.outerWidth / 2 + win.top.screenX - w / 2;
        let width = w;
        let height = h;
        if (x < 0) {
            width = 1080;
            height = 800;
        }
        return win.open(
            url,
            windowName,
            `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${y}, left=${x}`
        );
    };
    prepareTableData = (tableRecords) => {
        const BSTableData = [];
        tableRecords
            .sort((a, b) => {
                const dateA: any = new Date(a.updatedDate);
                const dateB: any = new Date(b.updatedDate);
                return dateB - dateA;
            })
            .forEach((checkoutItem) => {
                const newItem = {};
                newItem["id"] = checkoutItem.id;
                newItem["warrantyNum"] = checkoutItem.warrantyNumber;
                const seperator =
                    checkoutItem.owner && checkoutItem.owner.lastName && checkoutItem.owner.firstName ? ", " : "";
                newItem["buildingAddress"] = checkoutItem.buildingAddress.addressLine1;
                newItem["owner"] = checkoutItem.owner
                    ? checkoutItem.owner.lastName + seperator + checkoutItem.owner.firstName
                    : "";
                newItem["region"] = checkoutItem.buildingAddress
                    ? checkoutItem.buildingAddress.city + ", " + checkoutItem.buildingAddress.stateOrProvince
                    : "";
                newItem["warrantyType"] = checkoutItem.warrantyType;
                newItem["summaryPDFData"] = checkoutItem.summaryPdfData;
                newItem["canGenerateFullReport"] = checkoutItem.canGenerateFullReport;
                newItem["forminstanceTypeId"] = checkoutItem.forminstanceTypeId;
                newItem["postedDate"] = dateFormatter(checkoutItem.postedDate);
                newItem["pdTransactionId"] = checkoutItem.paymentDirectorTransactionId;
                newItem["actions"] = "Actions";
                BSTableData.push(newItem);
            });
        return BSTableData;
    };
    rankFormatter = (cell, row, rowIndex) => {
        return (
            <div
                id={`actions-row-${row.id}`}
                className="actions-icons"
                onClick={(e) => {
                    e.stopPropagation();
                    // this.onTableRowDelete(row);
                }}
            >
                <div className="copy-btn-with-icon" title="Copy">
                    <a onClick={() => this.copyMe(row)}></a>
                </div>
                <div className="download-summary-with-icon" title="Download Summary">
                    <a onClick={() => this.downloadSummaryPdf(row)}></a>
                </div>
                {!!row.canGenerateFullReport && (
                    <div className="download-btn-with-icon" title="Download Full Warranty">
                        <a onClick={() => this.downloadFullPdf(row)}></a>
                    </div>
                )}
                {/* <div className="payment-receipt-icon" title="Payment Receipt">
                    <a onClick={() => this.onPrintReceiptClick(row)}></a>
                </div> */}
            </div>
        );
    };
    renderMultipleWarranties(isMobileView) {
        const BSTableData = this.prepareTableData(this.props.transactionData);
        constantFile.thankYouGridColumns[6]["formatter"] = this.rankFormatter;
        const tableColumns = constantFile.thankYouGridColumns;
        const defaultSorted = constantFile.draftTableDefaultSorted;
        const displayClass = BSTableData.length === 0 ? "invisible" : "visible";
        return (
            <React.Fragment>
                <div className="submitted-list">
                    <div className={displayClass}>
                        {!isMobileView ? (
                            <div>
                                <BSTable
                                    hover
                                    striped
                                    sortable
                                    bordered={false}
                                    defaultSorted={defaultSorted}
                                    tableColumnData={tableColumns}
                                    tableRowData={BSTableData}
                                    tableRowClick={() => {}}
                                // tableRowDelete={this.onTableRowDelete}
                                />
                            </div>
                        ) : (
                            <div>
                                <div className="item-list">
                                    {BSTableData.map((tableItem, index) => {
                                        return (
                                            <div
                                                id={`table-row-id-${tableItem.id}`}
                                                key={`menuitem${index}`}
                                                className={`item d-flex menu-item mobile-view-item table-row-id-${tableItem.id}`}
                                            // onClick={() => this.props.onTableRowClick(tableItem)}
                                            >
                                                <div className="flex-coloumn mobile-view-column">
                                                    <div className="address">{tableItem.buildingAddress}</div>
                                                    <div className="address">{tableItem.region}</div>
                                                    <div className="date">{dateFormatter(tableItem.postedDate)}</div>
                                                </div>
                                                <div
                                                    className="delete-btn-menu actions-icons"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    <div className="copy-btn-with-icon" title="Copy">
                                                        <a onClick={() => this.copyMe(tableItem)}></a>
                                                    </div>
                                                    <div
                                                        className="download-summary-with-icon"
                                                        title="Download Summary"
                                                    >
                                                        <a onClick={() => this.downloadSummaryPdf(tableItem)}></a>
                                                    </div>
                                                    {!!tableItem.canGenerateFullReport && (
                                                        <div
                                                            className="download-btn-with-icon"
                                                            title="Download Full Warranty"
                                                        >
                                                            <a onClick={() => this.downloadFullPdf(tableItem)}></a>
                                                        </div>
                                                    )}
                                                    {/* <div className="payment-receipt-icon" title="Payment Receipt">
                                                        <a onClick={() => this.openPaymentReceiptTab(tableItem)}></a>
                                                    </div> */}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </React.Fragment>
        );
    }
    renderWaitingOnProcess = () => {
        return (
            <>
                <Loader />
                <div className="row">
                    <div className="text-center content proccess-waiting">
                        <h1 className="thk-u-lbl w-100">Thank You!</h1>
                        <div className="description mt-1 mb-2">
                            {`Your payment has been successfully processed. Please wait while your warranties are generated. Please do not refresh this page or click
                            your browser's back button.`}
                        </div>
                    </div>
                </div>
            </>
        );
    };
    render() {
        window.addEventListener("resize", this.handleResize);
        const { userInfo, transactionData, isDownloading, isLayoutCallInProcess} = this.props;
        const isAdmin = userInfo && userInfo.isAdmin;
        const isTm = userInfo && userInfo.isTm;
        const txRef = this.getTransactionId();
        let WarrantyNumber;
        let shouldShowDetails = true;
        if(transactionData && transactionData.length > 0) {
            if(!transactionData[0].isVarianceWaiverRequest) {
                shouldShowDetails = true;
            } else {
                if (transactionData[0].isVarianceWaiverRequest && (transactionData[0].varianceWaiverStatus === varianceWaiverStatusEnum["Pending"] ||
                transactionData[0].varianceWaiverStatus === varianceWaiverStatusEnum["Denied"])) {
                    shouldShowDetails = false;
                } else {
                    shouldShowDetails = true;
                }
            }
            WarrantyNumber = transactionData[0].warrantyNumber;
        }
        return (
            <>
                {isLayoutCallInProcess && (<Loader />)}
                <div
                    className={`container multiple-warranties ${
                        isAdmin || isTm ? "admin-thank-you-container" : "thank-you-container"
                        }`}
                >
                    {transactionData && transactionData.length > 0 ? (
                        <div className="row">
                            <div className="text-center content">
                                <h1 className="thk-u-lbl">Thank You!</h1>
                                <div className="success-lbl mt-1">
                                    Your shopping cart warranty registrations have been submitted.
                                </div>
                                <div className="success-description mt-1 mb-2">
                                    <div className="success-description-text">
                                        You can visit the{" "}
                                        <button
                                            className="remove-btn-style submitted-tab-link"
                                            onClick={this.openSubmittedTab}
                                        >
                                            Submitted Warranties
                                        </button>{" "}
                                        tab on your Dashboard to view the status of submitted warranties or to download
                                        warranties.
                                    </div>
                                    {(transactionData && transactionData[0].isVarianceWaiverRequest && !transactionData[0].canGenerateFullReport) && (
                                        <div className="variance-message-div">
                                            <p>
                                                {transactionData[0].message}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="shopping-cart-number">
                                    <>
                                        <p>
                                            <span className="lbl">Shopping cart number:</span> {this.props.cartId}
                                        </p>
                                        {(transactionData && transactionData[0].isVarianceWaiverRequest && !transactionData[0].canGenerateFullReport) && (
                                            <p>
                                                <span className="lbl">Warranty Number:</span> {WarrantyNumber}
                                            </p>
                                        )}
                                    </>
                                </div>
                                {(transactionData && shouldShowDetails)
                                 && (
                                    <>
                                        <div className={!!txRef ? "payment-reciept" : "d-none"}>
                                            <span className="payment-receipt-icon" title="Payment Receipt">
                                                <a
                                                    onClick={() => {
                                                        console.log("open payment receipt");
                                                    }}
                                                ></a>
                                            </span>
                                            <button
                                                className="remove-btn-style payment-receipt-btn"
                                                onClick={this.onPrintReceiptClick}
                                            >
                                                View Payment Receipt
                                            </button>
                                        </div>
                                        <div className="create-copy-buttons">
                                            <button
                                                type="button"
                                                tabIndex={10}
                                                id="create-new-button"
                                                className="create-new new-warranty-button"
                                                onClick={this.createNew}
                                            >
                                                Create New
                                            </button>
                                            <button
                                                type="button"
                                                disabled={isDownloading}
                                                tabIndex={10}
                                                id="download-all-button"
                                                className="create-new copy-warranty-button download-all-btn"
                                                onClick={this.downloadAllClick}
                                            >
                                                Download All Warranties
                                            </button>
                                        </div>
                                        {this.renderMultipleWarranties(this.props.screenResolution.width < 768)}
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        this.renderWaitingOnProcess()
                    )}
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: RootState): ThankYouProps => {
    return {
        userInfo: state.adminData.userInfo,
        screenResolution: state.globalInfo.screenResolution,
        error: state.thankYouInfo.isTransactionFailed,
        transactionData: state.thankYouInfo.transactionData,
        cartId: state.thankYouInfo.cartId,
        reviewData: state.dynamicFormInfo.reviewAndPayData,
        isDownloading: state.thankYouInfo.isDownloading,
        tempContractorSelection: state.adminData.tempContractorSelection,
        layoutResponse: state.dynamicFormInfo.layoutResponse,
        isLayoutCallInProcess: state.dynamicFormInfo.isLayoutCallInProcess,
    };
};

const mapDispatchToProps = (dispatch: Dispatch): ThankYouActionProps => {
    return {
        fetchUserInfo: bindActionCreators(adminActionCreators.fetchUserInfo, dispatch),
        updateContractorSelection: bindActionCreators(adminActionCreators.updateContractorSelection, dispatch),
        validateTransaction: bindActionCreators(ACTIONS.validateTransaction, dispatch),
        clearTransactionData: bindActionCreators(ACTIONS.clearTransactionData, dispatch),
        updateActiveTab: bindActionCreators(submittedWarrantyActionCreators.updateActiveTab, dispatch),
        updateActiveScreen: bindActionCreators(globalActions.updateActiveScreen, dispatch),
        generateFullWarrantyPdf: bindActionCreators(ACTIONS.generateFullWarranty, dispatch),
        updateScreenResolution: bindActionCreators(globalActions.updateScreenResolution, dispatch),
        generateSummaryWarrantyPdf: bindActionCreators(ACTIONS.generateSummaryWarranty, dispatch),
        downloadWarrantySummaryPdf: bindActionCreators(PAYMENT_ACTIONS.downloadSummaryPdf, dispatch),
        updateIsAlreadyPaymentInitiated: bindActionCreators(ACTIONS.updateIsAlreadyPaymentInitiated, dispatch),
        resetIsAlreadyPaymentInitiated: bindActionCreators(PAYMENT_ACTIONS.updateIsAlreadyPaymentInitiated, dispatch),
        downloadAllWarranties: bindActionCreators(ACTIONS.downloadAllWarranties, dispatch),
        fetchFormLayoutData: bindActionCreators(dynamicActionCreators.fetchFormLayoutData, dispatch),
    };
};

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(MultipleWarrantiesThankYouComponent);
